import { useState } from 'react';
import { Screen } from '../components/ui.js';
import { ApiError, setDisplayName } from '../lib/api.js';
import { copyText } from '../lib/export.js';
import { readIdentity, updateDisplayName } from '../lib/identity.js';
import { readThemePref, setThemePref, type ThemePref } from '../lib/theme.js';


const THEME_OPTIONS: [ThemePref, string][] = [
  ['system', 'Match device'],
  ['light', 'Light'],
  ['dark', 'Dark'],
];

export function Settings() {
  const identity = readIdentity();
  const [name, setName] = useState(identity?.displayName ?? '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState<ThemePref>(readThemePref);

  const chooseTheme = (pref: ThemePref) => {
    setThemePref(pref);
    setTheme(pref);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const result = await setDisplayName(name.trim());
      updateDisplayName(result.displayName);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that name');
    }
  };

  const copy = async () => {
    if (!identity) return;
    if (await copyText(identity.token)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    }
  };

  return (
    <Screen title="Settings" back="/">
      <form className="card" onSubmit={save}>
        <label htmlFor="display-name">Name</label>
        <div className="inline-form">
          <input
            id="display-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={24}
            placeholder="Your name"
          />
          <button type="submit" className="button small" disabled={!name.trim()}>
            {saved ? 'Saved' : 'Save'}
          </button>
        </div>
        <p className="muted small">Other players see this. Change it whenever you like.</p>
        {error ? <p className="error-text">{error}</p> : null}
      </form>

      <div className="card">
        <label>Appearance</label>
        <div className="chips" role="radiogroup" aria-label="Appearance">
          {THEME_OPTIONS.map(([value, text]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={theme === value}
              className={`chip ${theme === value ? 'selected' : ''}`}
              onClick={() => chooseTheme(value)}
            >
              {text}
            </button>
          ))}
        </div>
        <p className="muted small">Drawings always stay black on white.</p>
      </div>

      <div className="card">
        <label>Recovery code</label>
        <p className="muted small">
          This is the only way back to your comics on a new phone. Save it somewhere safe — anyone with it can play as
          you.
        </p>
        {revealed ? (
          <pre className="recovery-code">{identity?.token}</pre>
        ) : (
          <button type="button" className="button" onClick={() => setRevealed(true)}>
            Show my code
          </button>
        )}
        <button type="button" className="button primary" onClick={copy}>
          {copied ? 'Copied' : 'Copy recovery code'}
        </button>
      </div>

      {/* Exactly what is live: bumped and stamped by `npm run deploy:web`. */}
      <p className="muted small center">
        Panels v{__APP_VERSION__} · built {__BUILD_DATE__} · <span className="mono">{__BUILD_COMMIT__}</span>
      </p>
    </Screen>
  );
}
